import classnames from 'classnames/bind'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { useTranslate } from 'react-polyglot'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalGridPreset } from '~/@types/grid-preset'
import { TRACKING_EVENTS } from '~/lib/constants'
import {
  SHOPIFY_SEARCH_COMMON_FILTER,
  SHOPIFY_SEARCH_FASHION_FILTER,
  SHOPIFY_SEARCH_FRAGRANCE_FILTER,
  SHOPIFY_SEARCH_SET_FILTER,
} from '~/lib/shopify/constants'

import { Spinner } from '@unlikelystudio/react-abstract-components'

import ResultGridsContainer from '~/components/Slices/Search/ResultGridsContainer'
import NoResults from '~/components/UI/NoResults'

import { useShopifySearchContext } from '~/providers/ShopifySearchProvider'
import { useStyle } from '~/providers/StyleProvider'
import { useTracker } from '~/providers/TrackerProvider'

import useDebounce from '~/hooks/useDebounce'
import { useShopifySearch } from '~/hooks/useShopifySearch'

import { stringIsNotEmpty } from '~/utils/check-empty-string'

import { SEARCH } from '~/data/dictionary'

import SearchInput from './SearchInput'
import css from './styles.module.scss'

const cx = classnames.bind(css)

export type UrlParamsProps = {
  q?: string
}

export const SEARCH_HITS_PER_PAGE = 6

function Search() {
  const { tracker } = useTracker()
  const gridStyle = useStyle({ grid: GlobalGridPreset.BASE_GRID })
  const router = useRouter()
  const t = useTranslate()

  const { query } = useShopifySearchContext()
  const [value, setValue] = useState(query)
  const debouncedValue = useDebounce(value, 150)
  const [isLoading, setIsLoading] = useState(false)
  const [isSearching, setIsSearching] = useState(true)

  useEffect(() => {
    setIsSearching(value?.length > 0)
  }, [value])

  // Getting the Fashion products
  const {
    data: fashionData,
    fetchNextPage: fashionFetchNextPage,
    hasNextPage: fashionHasNextPage,
    isFetching: fashionIsFetching,
  } = useShopifySearch({
    query: debouncedValue,
    filters: stringIsNotEmpty(debouncedValue)
      ? [SHOPIFY_SEARCH_FASHION_FILTER, SHOPIFY_SEARCH_COMMON_FILTER]
      : [SHOPIFY_SEARCH_COMMON_FILTER],
    key: 'search_shopify_fashion_products',
    hitsPerPage: SEARCH_HITS_PER_PAGE,
  })

  // Getting the Fragrance products
  const {
    data: fragranceData,
    fetchNextPage: fragranceFetchNextPage,
    hasNextPage: fragranceHasNextPage,
    isFetching: fragranceIsFetching,
  } = useShopifySearch({
    query: debouncedValue,
    filters: [
      SHOPIFY_SEARCH_FRAGRANCE_FILTER,
      SHOPIFY_SEARCH_SET_FILTER,
      SHOPIFY_SEARCH_COMMON_FILTER,
    ],
    enabled: stringIsNotEmpty(debouncedValue),
    key: 'search_shopify_fragrance_products',
    hitsPerPage: SEARCH_HITS_PER_PAGE,
  })

  // Getting the hits number
  const debouncedNbHits =
    (fashionData?.pages?.[0]?.nbHits ?? 0) +
    (fragranceData?.pages?.[0]?.nbHits ?? 0)

  // Getting the pages number
  const debouncedNbPages =
    (fashionData?.pages?.length ?? 0) + (fragranceData?.pages?.length ?? 0)

  // Storing everything
  const memoizedDebouncedShopifyData = useMemo(
    () => ({
      debouncedNbHits: debouncedNbHits?.toString(),
      debouncedNbPages: debouncedNbPages?.toString(),
      debouncedValue,
    }),
    [debouncedNbHits, debouncedNbPages, debouncedValue],
  )

  useEffect(() => {
    if (debouncedValue?.length >= 3) {
      const interactionData = {
        search: {
          category: 'product',
          origin: router.pathname,
          pageNumber: memoizedDebouncedShopifyData.debouncedNbPages,
          resultsNumber: memoizedDebouncedShopifyData.debouncedNbHits,
          term: debouncedValue,
        },
      }

      tracker.emit(TRACKING_EVENTS.PAGE_VIEW)

      tracker.emit(TRACKING_EVENTS.INTERACTION_TAGGED_EVENT, {
        eventName: 'internalSearch',
        eventAction: 'internal search',
        eventCategory: 'user actions',
        eventLabel: debouncedValue?.replaceAll("'", ' '),
        interaction: interactionData,
      })
    }
  }, [debouncedValue, memoizedDebouncedShopifyData])

  useEffect(
    () => setIsLoading(fashionIsFetching && fragranceIsFetching),
    [fashionIsFetching, fragranceIsFetching],
  )

  // Reducing the results
  const fashionResults =
    fashionData?.pages?.reduce((acc, page) => {
      if (page) {
        return [...acc, ...page.serializedProductCards]
      } else {
        return [...acc]
      }
    }, []) ?? []

  const fragranceResults =
    fragranceData?.pages?.reduce((acc, page) => {
      if (page) {
        return [...acc, ...page.serializedProductCards]
      } else {
        return [...acc]
      }
    }, []) ?? []

  const fashionNbHits = fashionData?.pages?.[0]?.nbHits ?? 0
  const fragranceNbHits = fragranceData?.pages?.[0]?.nbHits ?? 0

  // Assign initial results (best sellers) to const with more explicit names
  const [bestSellersFashionResults] = [fashionResults]

  const hasFashionResults = fashionResults?.length > 0
  const hasFragranceResults = fragranceResults?.length > 0

  const renderGridResults = () => {
    const isFashionBigger = fashionResults?.length >= fragranceResults?.length

    const items = [
      <ResultGridsContainer
        position={isFashionBigger ? 0 : fragranceResults?.length}
        key={'fashion_results'}
        hasResults={hasFashionResults}
        title={t(SEARCH.FASHION_RESULTS)}
        count={fashionNbHits}
        cards={fashionResults}
        canLoadMore={fashionHasNextPage}
        onLoadMore={fashionFetchNextPage}
      />,
      <ResultGridsContainer
        position={isFashionBigger ? fashionResults?.length : 0}
        key="fashion_results"
        hasResults={hasFragranceResults}
        title={t(SEARCH.FRAGRANCE_RESULTS)}
        count={fragranceNbHits}
        cards={fragranceResults}
        canLoadMore={fragranceHasNextPage}
        onLoadMore={fragranceFetchNextPage}
      />,
    ]
    return isFashionBigger ? items : items.reverse()
  }

  return (
    <section className={cx(css.Search)}>
      <div className={cx(css.wrapper)}>
        <div className={cx(css.searchFormWrapper, gridStyle)}>
          <SearchInput
            value={value}
            className={css.searchInput}
            isSearching={isSearching}
            onChange={setValue}
          />
        </div>
        {isLoading ? (
          <div className={css.loader}>
            <Spinner color={GlobalThemeColors.Black} className={css.spinner} />
          </div>
        ) : (
          <>
            {!isSearching && (
              <ResultGridsContainer
                position={0}
                key="best_sellers"
                hasResults={bestSellersFashionResults?.length > 0}
                title={t(SEARCH.BEST_SELLERS_RESULTS)}
                count={bestSellersFashionResults?.length}
                cards={bestSellersFashionResults}
                canLoadMore={false}
              />
            )}
            {isSearching && renderGridResults()}
            {isSearching &&
              !hasFashionResults &&
              !hasFragranceResults &&
              !fashionIsFetching &&
              !fragranceIsFetching && (
                <NoResults className={css.resultsWrapper} title={''} />
              )}
          </>
        )}
      </div>
    </section>
  )
}

Search.defaultProps = {}

export default Search
